import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
	useLocation,
} from 'react-router'
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '#app/components/ui/alert.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { getErrorMessage } from '#app/utils/misc.tsx'
import {
	capturePostHogError,
	capturePostHogRouteError,
} from '#app/utils/posthog-error.client.ts'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<Alert variant="destructive">
			<Icon name="exclamation-triangle" />
			<AlertTitle>Error {error.status}</AlertTitle>
			<AlertDescription>{error.data.message || error.data}</AlertDescription>
		</Alert>
	),
	statusHandlers = {
		403: ({ error }) => (
			<Alert variant="destructive">
				<Icon name="exclamation-triangle" />
				<AlertTitle>Access Denied</AlertTitle>
				<AlertDescription>
					{error.data.message ||
						'You do not have permission to view this page.'}
				</AlertDescription>
			</Alert>
		),
	},
	unexpectedErrorHandler = (error: unknown) => (
		<Alert variant="destructive">
			<Icon name="exclamation-triangle" />
			<AlertTitle>Error</AlertTitle>
			<AlertDescription>{getErrorMessage(error)}</AlertDescription>
		</Alert>
	),
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const location = useLocation()
	const isResponse = isRouteErrorResponse(error)

	useEffect(() => {
		// Capture to Sentry (existing)
		if (!isResponse) {
			captureException(error)
		}

		// Capture to PostHog
		if (isResponse) {
			// Route errors (404, 403, 500, etc.)
			capturePostHogRouteError(error.status, location.pathname, error.data)
		} else {
			// Unexpected errors (JS exceptions, etc.)
			capturePostHogError(error, {
				pathname: location.pathname,
				route_id: location.pathname,
				params: JSON.stringify(params),
			})
		}
	}, [error, isResponse, location.pathname, params])

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	return (
		<div className="container mt-8 flex items-center justify-center">
			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
